import React from 'react';
import { Lightbulb, Minus, Plus } from 'lucide-react';
interface Product {
  id: string;
  name: string;
  image: string;
  description: string;
}
interface Category {
  id: string;
  name: string;
  icon: React.ReactNode;
  products: Product[];
}
const categories: Category[] = [{
  id: 'portapotties',
  name: 'Portable Toilets',
  icon: <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/oY4it37TfPkXAS5XYY5L5D/image.png" alt="" className="w-8 h-8 object-contain opacity-80" data-id="element-147" />,
  products: [{
    id: 'standard',
    name: 'Standard Unit',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/oY4it37TfPkXAS5XYY5L5D/image.png',
    description: 'Basic portable toilet unit'
  }, {
    id: 'handicap',
    name: 'Handicap Accessible',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/oY4it37TfPkXAS5XYY5L5D/image.png',
    description: 'ADA compliant accessible unit'
  }, {
    id: 'trailer',
    name: 'Luxury Trailer',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/oY4it37TfPkXAS5XYY5L5D/image.png',
    description: 'Multi-unit luxury trailer'
  }]
}, {
  id: 'fences',
  name: 'Temporary Fencing',
  icon: <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3Ar8fgRzoRKRwo53V2jfN1/fence.png" alt="" className="w-8 h-8 object-contain opacity-80" data-id="element-148" />,
  products: [{
    id: 'chain-link',
    name: 'Chain Link',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3Ar8fgRzoRKRwo53V2jfN1/fence.png',
    description: 'Standard chain link fencing'
  }, {
    id: 'privacy',
    name: 'Privacy Screen',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3Ar8fgRzoRKRwo53V2jfN1/fence.png',
    description: 'Solid privacy screening'
  }, {
    id: 'crowd-control',
    name: 'Crowd Control',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3Ar8fgRzoRKRwo53V2jfN1/fence.png',
    description: 'Portable crowd control barriers'
  }]
}, {
  id: 'lighting',
  name: 'Industrial Lighting',
  icon: <Lightbulb className="w-6 h-6" data-id="element-149" />,
  products: [{
    id: 'tower',
    name: 'Light Tower',
    image: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='lucide lucide-lightbulb'%3E%3Cpath d='M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5'/%3E%3Cpath d='M9 18h6'/%3E%3Cpath d='M10 22h4'/%3E%3C/svg%3E",
    description: 'Mobile light tower'
  }, {
    id: 'string',
    name: 'String Lights',
    image: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='lucide lucide-lightbulb'%3E%3Cpath d='M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5'/%3E%3Cpath d='M9 18h6'/%3E%3Cpath d='M10 22h4'/%3E%3C/svg%3E",
    description: 'Construction string lighting'
  }, {
    id: 'flood',
    name: 'Flood Lights',
    image: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='lucide lucide-lightbulb'%3E%3Cpath d='M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5'/%3E%3Cpath d='M9 18h6'/%3E%3Cpath d='M10 22h4'/%3E%3C/svg%3E",
    description: 'Industrial flood lights'
  }]
}];
interface ProductSelection {
  productId: string;
  quantity: number;
}
interface ProductSelectorProps {
  selectedCategory: string;
  selectedProducts: ProductSelection[];
  onCategoryChange: (category: string) => void;
  onProductChange: (selections: ProductSelection[]) => void;
}
export const ProductSelector: React.FC<ProductSelectorProps> = ({
  selectedCategory,
  selectedProducts,
  onCategoryChange,
  onProductChange
}) => {
  const currentCategory = categories.find(c => c.id === selectedCategory);
  const handleQuantityChange = (productId: string, delta: number) => {
    const currentSelections = [...selectedProducts];
    const existingIndex = currentSelections.findIndex(s => s.productId === productId);
    if (existingIndex >= 0) {
      const newQuantity = currentSelections[existingIndex].quantity + delta;
      if (newQuantity <= 0) {
        currentSelections.splice(existingIndex, 1);
      } else {
        currentSelections[existingIndex].quantity = newQuantity;
      }
    } else if (delta > 0) {
      currentSelections.push({
        productId,
        quantity: 1
      });
    }
    onProductChange(currentSelections);
  };
  const getQuantityForProduct = (productId: string) => {
    return selectedProducts.find(s => s.productId === productId)?.quantity || 0;
  };
  return <div className="space-y-3" data-id="element-150">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-151">
        {categories.map(category => <div key={category.id} className="space-y-2" data-id="element-152">
            <button type="button" onClick={() => onCategoryChange(category.id)} className={`w-full p-3 rounded-lg border transition-all hover:shadow-sm ${selectedCategory === category.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`} data-id="element-153">
              <div className="flex items-center md:flex-col md:items-center gap-2" data-id="element-154">
                <div className={`rounded-lg p-2 transition-colors ${selectedCategory === category.id ? 'text-orange-500 bg-orange-50' : 'text-muted-foreground bg-secondary/50'}`} data-id="element-155">
                  {category.icon}
                </div>
                <div className="font-medium text-sm" data-id="element-156">{category.name}</div>
              </div>
            </button>
            {selectedCategory === category.id && <div className="grid grid-cols-1 gap-1.5" data-id="element-157">
                {category.products.map(product => {
            const quantity = getQuantityForProduct(product.id);
            return <div key={product.id} className={`relative group overflow-hidden rounded-lg border transition-all ${quantity > 0 ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/50'}`} data-id="element-158">
                      <div className="flex h-full items-center py-1.5" data-id="element-159">
                        <div className="w-[40px] flex items-center justify-center" data-id="element-160">
                          <img src={product.image} alt={product.name} className="w-5 h-5 object-contain" data-id="element-161" />
                        </div>
                        <div className="flex-1" data-id="element-162">
                          <h3 className="font-medium text-sm" data-id="element-163">
                            {product.name}
                          </h3>
                        </div>
                        <div className="flex items-center gap-1 pr-2" data-id="element-164">
                          <button type="button" onClick={() => handleQuantityChange(product.id, -1)} className="p-1 rounded-md hover:bg-secondary/80 disabled:opacity-50" disabled={quantity === 0} data-id="element-165">
                            <Minus className="w-4 h-4" data-id="element-166" />
                          </button>
                          <span className="w-5 text-center text-sm" data-id="element-167">
                            {quantity}
                          </span>
                          <button type="button" onClick={() => handleQuantityChange(product.id, 1)} className="p-1 rounded-md hover:bg-secondary/80" data-id="element-168">
                            <Plus className="w-4 h-4" data-id="element-169" />
                          </button>
                        </div>
                      </div>
                    </div>;
          })}
              </div>}
          </div>)}
      </div>
    </div>;
};